import Mobile from '@components/Hero/Detail/Mobile';
import ImageComponent from '@components/common/ImageComponent/ImageComponent';
import IVButton from '@invideoio/uniformd/components/Button';
import { PlatformType, OSType } from '@invideoio/web-shared/constants/common';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import { deviceStateSelector } from '@store/device-state';
import { useAppSelector } from '@store/hook';
import React, { useMemo, useState } from 'react';
import { ClickCtaType, ClickCtaUxSource } from 'src/types/analytics';
import { MobileAppLink, SideTextType } from 'src/types/landing-page';

interface DetailsProps {
  sideText: SideTextType;
  buttonText: string;
  CTAUrl: string;
  subText: string;
  allowEditor: boolean;
  hideEmailOnMobile?: boolean;
  MobileAppLink?: MobileAppLink;
  tempalteData?: Record<string, any>;
  onSignUp: Function;
  showMobileAppCTA: boolean;
}

const Details = (props: DetailsProps) => {
  const {
    sideText,
    buttonText,
    CTAUrl,
    subText,
    allowEditor,
    hideEmailOnMobile,
    MobileAppLink,
    tempalteData,
    onSignUp,
    showMobileAppCTA,
  } = props;

  const [ctaRef, setCtaRef] = useState<any>(null);
  const deviceState = useAppSelector(deviceStateSelector);

  const { platform, os } = deviceState;

  const isIOS = os === OSType.iOS;
  const isAndroid = os === OSType.Android;
  const showMobileCTA = showMobileAppCTA && (isIOS || isAndroid);
  const defaultMobileAppURL = 'https://invideo-ai.app.link/hp-hero-cta';

  const {
    AndroidAppCTA,
    AndroidAppLink,
    IOSAppCTA,
    IOSAppLink,
    WebCTAOnMobile,
  } = MobileAppLink || {
    AndroidAppCTA: '',
    AndroidAppLink: '',
    IOSAppCTA: '',
    IOSAppLink: '',
    WebCTAOnMobile: '',
  };

  const webCTAOnMobile = WebCTAOnMobile || 'Try on web';
  const androidAppCTA = AndroidAppCTA || 'Get Android app';
  const iosAppCTA = IOSAppCTA || 'Get iOS app';
  const mobileCTA = isAndroid ? androidAppCTA : iosAppCTA;
  const webCTA = showMobileCTA ? webCTAOnMobile : buttonText;

  const mobileAppURL = isAndroid
    ? AndroidAppLink
    : IOSAppLink || defaultMobileAppURL;

  const onClick = () => {
    onSignUp({
      ux_source: ClickCtaUxSource.Top,
      type: ClickCtaType.ClickSignup,
      btn_position: 'Top',
      oa: new URL(CTAUrl).searchParams.get('oa') ?? undefined,
    });
  };

  return (
    <React.Fragment>
      <div
        className={ivclass(
          'iv-inline-block',
          'iv-pb-50 md:iv-pb-50 sm:iv-pb-40',
          'lg:iv-mb-4 md:iv-mb-3 lg:iv-mr-4 md:iv-px-10',
          'xl:iv-text-heading-3xs lg:iv-text-heading-4xs md:iv-text-heading-4xs sm:iv-text-body-2xl',
          'xl:iv-m-heading-3xs lg:iv-m-heading-4xs md:iv-m-heading-4xs sm:iv-m-body-2xl',
          'iv-font-semibold',
        )}
      >
        <span
          className={ivclass('iv-text-black-85', 'md:iv-inline sm:iv-inline')}
        >
          {sideText.highlightedText}
        </span>
        <span
          className={ivclass('iv-text-black-50', 'md:iv-inline sm:iv-inline')}
        >
          {sideText.plainText1}
        </span>
        <div
          className={ivclass('iv-text-black-50', 'md:iv-inline sm:iv-inline')}
        >
          {sideText.plainText2}
        </div>
      </div>

      {MobileAppLink ? (
        <div className="iv-text-center iv-flex iv-items-center md:iv-justify-center  sm:iv-justify-center sm:iv-flex-col iv-gap-30 md:iv-mb-40 sm:iv-mb-30">
          {MobileAppLink.QRCode  && <div className="">
            <ImageComponent
              elementype="native"
              src={MobileAppLink.QRCode.url}
              alt="QR Code"
              className="iv-w-[124px] iv-h-[124px]"
            />
          </div>}
          <div className="iv-flex iv-flex-col">
            {showMobileCTA && (
              <IVButton
                ref={setCtaRef}
                className={ivclass(
                  'iv-flex',
                  'iv-justify-center',
                  'iv-items-center',
                  'iv-cursor-pointer',
                  'iv-h-65 sm:iv-h-52',
                  'iv-rounded-full',
                  'iv-bg-blue hover:iv-bg-blue-strong iv-transition',
                  'iv-px-[24px] iv-py-20 sm:iv-px-20',
                  'iv-text-grey-0',
                  'iv-text-body-3xl sm:iv-text-body-2xl',
                  'iv-font-bold sm:iv-font-semibold',
                  'iv-mx-auto',
                )}
                type="button"
                onClick={() => {
                  window.open(mobileAppURL, '_blank');
                }}
              >
                {mobileCTA}
              </IVButton>
            )}
            {CTAUrl && CTAUrl.startsWith('#') ? (
              <a
                className={ivclass(
                  'iv-flex',
                  'iv-justify-center',
                  'iv-items-center',
                  'iv-cursor-pointer',
                  'iv-h-65 sm:iv-h-52',
                  'iv-rounded-full iv-transition',
                  {
                    'iv-mt-10 iv-border-solid iv-border-2 iv-border-blue':
                      showMobileCTA,
                    'iv-bg-transparent hover:iv-bg-blue iv-text-blue':
                      showMobileCTA,
                    'iv-bg-blue hover:iv-bg-blue-strong iv-text-grey-0':
                      !showMobileCTA,
                  },
                  'iv-px-[24px] iv-py-20 sm:iv-px-20',
                  'iv-text-body-3xl sm:iv-text-body-2xl',
                  'iv-font-bold sm:iv-font-semibold',
                  'iv-mx-auto',
                  'iv-w-max',
                )}
                href={CTAUrl}
              >
                {webCTA}
              </a>
            ) : (
              <IVButton
                ref={setCtaRef}
                className={ivclass(
                  'iv-flex',
                  'iv-justify-center',
                  'iv-items-center',
                  'iv-cursor-pointer',
                  'iv-h-65 sm:iv-h-52',
                  'iv-rounded-full iv-transition',
                  {
                    'iv-mt-10 iv-border-solid iv-border-2 iv-border-blue':
                      showMobileCTA,
                    'iv-bg-transparent hover:iv-bg-blue iv-text-blue':
                      showMobileCTA,
                    'iv-bg-blue hover:iv-bg-blue-strong iv-text-grey-0':
                      !showMobileCTA,
                  },
                  'iv-px-[24px] iv-py-20 sm:iv-px-20',
                  'iv-text-body-3xl sm:iv-text-body-2xl',
                  'iv-font-bold sm:iv-font-semibold',
                  'iv-mx-auto',
                )}
                type="button"
                onClick={onClick}
              >
                {webCTA}
              </IVButton>
            )}
          </div>
        </div>
      ) : (
        <div className="iv-inline-block iv-text-center">
          {platform !== PlatformType.Desktop && !hideEmailOnMobile ? (
            <Mobile
              CTAText="Get Update"
              inputPlaceholder="Enter Email"
              subInfo="We'll send you mail when we launch for mobile"
              tempalteData={tempalteData}
            />
          ) : (
            <IVButton
              className={ivclass(
                'iv-flex',
                'iv-justify-center',
                'iv-items-center',
                'iv-cursor-pointer',
                'iv-h-65 sm:iv-h-52',
                'iv-rounded-full',
                'iv-bg-blue hover:iv-bg-blue-strong iv-transition',
                'iv-px-40 sm:iv-px-25',
                'iv-mb-6 sm:iv-mb-10 md:iv-mx-auto sm:iv-mx-auto',
                'iv-text-grey-0',
                'iv-text-body-3xl sm:iv-text-body-2xl',
                'iv-font-bold',
              )}
              onClick={onClick}
            >
              {buttonText}
            </IVButton>
          )}
          <div
            className={ivclass(
              'iv-pt-10 md:iv-pb-0 sm:iv-pt-1 sm:iv-pb-0',
              'md:iv-mx-auto sm:iv-mx-auto',
              'iv-text-grey-100',
              'iv-text-body-m sm:iv-text-body-xs',
              'iv-font-medium',
            )}
          >
            {subText}
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default Details;
